/**
 * Manual-choice conflicts of a Browse / Swap / Replace Both candidate.
 *
 * Phase 2B (backend b100d61) keeps these candidates LISTED and SELECTABLE on
 * the manual surfaces -- the server only demotes them -- so the UI must say
 * why, and a conflicted candidate must never look like an ordinary clean
 * recommendation. Everything here is read from the facts the backend already
 * returns; nothing is recomputed.
 *
 *   allergen           has_allergen (a matched token), or the Phase 2B
 *                      preference / pair `conflicts` (includes allergen safety
 *                      that could not be proven: fail-closed, often no token)
 *   dislike            has_dislike, or conflicts
 *   dont_suggest       preference.dont_suggest / dont_suggest, or conflicts
 *   missing_cookware   missing_cookware (equipment names on Browse / Swap
 *                      cards, a boolean on Replace Both candidates), or conflicts
 *   exceeds_cook_time  exceeds_cook_time, or conflicts
 *   same_day_family    preference.same_day_family / same_day_family /
 *                      is_repeat_family, or conflicts
 *   same_day_group     preference.same_day_group / same_day_group, or conflicts
 *
 * Returned in display priority: allergen first, then the existing Swap
 * warning order (dislike before cookware before cook time) with Don't Suggest
 * beside dislike (the backend's same preference tier), and the same-day
 * repeats last (the backend reports them, it does not tier them).
 */
import { cookwareLabel, COOKWARE_LABELS } from './cookwareLabels';

// Label copy. Existing app wording where there is one; entries marked
// PROVISIONAL are placeholders pending final product copy.
export var CONFLICT_LABELS = {
  allergen: 'Contains allergen',
  allergen_unverified: 'Allergens not verified', // PROVISIONAL
  dislike: 'Contains disliked ingredient',
  dont_suggest: "Marked Don't suggest", // PROVISIONAL
  missing_cookware: 'Missing cookware', // PROVISIONAL (equipment unknown)
  exceeds_cook_time: 'Over selected cook time',
  same_day_family: 'Already on this day', // PROVISIONAL
  same_day_family_pair: 'Already on these days', // PROVISIONAL
  same_day_group: 'Similar meal on this day', // PROVISIONAL
  same_day_group_pair: 'Similar meal on these days', // PROVISIONAL
};

/** "Needs <equipment>" for one missing cookware id. */
export function cookwareNeedLabel(cw) {
  var norm = String(cw || '').toLowerCase().replace(/[\s-]/g, '_');
  if (COOKWARE_LABELS[norm]) return 'Needs ' + cookwareLabel(norm);
  var pretty = String(cw || '').replace(/_/g, ' ').trim();
  return 'Needs ' + pretty;
}

function preferenceOf(meal) {
  return (meal && meal.preference) || {};
}

// The backend's own conflict names: preference.conflicts on Browse / Swap
// cards, the top-level `conflicts` list on Replace Both candidates.
function backendConflicts(meal) {
  var out = {};
  var pref = preferenceOf(meal);
  var list = Array.isArray(pref.conflicts) ? pref.conflicts
    : (meal && Array.isArray(meal.conflicts) ? meal.conflicts : []);
  list.forEach(function (k) { out[k] = true; });
  return out;
}

/** Allergen tokens the backend matched (empty for unverified allergen safety). */
export function allergenTokens(meal) {
  if (!meal) return [];
  var pref = preferenceOf(meal);
  var tokens = meal.matching_allergens || meal.matched_allergen_tokens;
  if (!tokens || !tokens.length) tokens = (pref.allergen && pref.allergen.tokens) || null;
  if (!tokens || !tokens.length) tokens = (meal.allergen_check && meal.allergen_check.tokens) || [];
  return tokens.slice();
}

/**
 * True for a matched allergen AND for allergen safety the backend could not
 * prove (Phase 2B reports both as the `allergen` conflict; `has_allergen` alone
 * misses the unverified case).
 */
export function hasAllergenConflict(meal) {
  if (!meal) return false;
  if (meal.has_allergen) return true;
  if (backendConflicts(meal).allergen) return true;
  var pref = preferenceOf(meal);
  if (pref.allergen && pref.allergen.unverified) return true;
  return !!(meal.allergen_check && meal.allergen_check.unverified);
}

/**
 * Every manual-choice conflict of one candidate, in display priority.
 * opts.scope: 'day' (Browse / Swap, default) or 'pair' (Replace Both: the
 * cook day and the leftover day). Each entry: { key, label, tone, detail }
 * with tone 'danger' (allergen), 'warn' (a preference conflict) or 'info'
 * (a same-day repeat), and detail = the matched names when known.
 */
export function getMealConflicts(meal, opts) {
  if (!meal) return [];
  var pair = !!(opts && opts.scope === 'pair');
  var c = backendConflicts(meal);
  var pref = preferenceOf(meal);
  var out = [];

  if (hasAllergenConflict(meal)) {
    var tokens = allergenTokens(meal);
    var unverified = !!((pref.allergen && pref.allergen.unverified) ||
      (meal.allergen_check && meal.allergen_check.unverified));
    out.push({ key: 'allergen', tone: 'danger', detail: tokens,
      label: (!tokens.length && unverified) ? CONFLICT_LABELS.allergen_unverified : CONFLICT_LABELS.allergen });
  }
  if (meal.has_dislike || c.dislike) {
    var disliked = meal.matching_dislikes || (pref.dislike && pref.dislike.tokens) || [];
    out.push({ key: 'dislike', tone: 'warn', label: CONFLICT_LABELS.dislike, detail: disliked.slice() });
  }
  if (pref.dont_suggest || meal.dont_suggest === true || c.dont_suggest) {
    out.push({ key: 'dont_suggest', tone: 'warn', label: CONFLICT_LABELS.dont_suggest, detail: [] });
  }
  var mc = meal.missing_cookware;
  var names = (Array.isArray(mc) && mc.length) ? mc : ((pref.cookware && pref.cookware.missing) || []);
  names = names.filter(function(name) { return String(name).toLowerCase().replace(/[\s-]/g, '_') !== 'no_cook'; });
  if (names.length > 0 || mc === true || (c.missing_cookware && !Array.isArray(mc))) {
    out.push({ key: 'missing_cookware', tone: 'warn', detail: names.slice(),
      label: names.length ? cookwareNeedLabel(names[0]) : CONFLICT_LABELS.missing_cookware });
  }
  if (meal.exceeds_cook_time || c.exceeds_cook_time) {
    out.push({ key: 'exceeds_cook_time', tone: 'warn', label: CONFLICT_LABELS.exceeds_cook_time, detail: [] });
  }
  if (pref.same_day_family || meal.same_day_family === true || meal.is_repeat_family === true || c.same_day_family) {
    out.push({ key: 'same_day_family', tone: 'info', detail: [],
      label: pair ? CONFLICT_LABELS.same_day_family_pair : CONFLICT_LABELS.same_day_family });
  }
  if (pref.same_day_group || meal.same_day_group === true || c.same_day_group) {
    out.push({ key: 'same_day_group', tone: 'info', detail: [],
      label: pair ? CONFLICT_LABELS.same_day_group_pair : CONFLICT_LABELS.same_day_group });
  }
  return out;
}

/** One conflict as display text: its label, plus the matched names if any. */
export function conflictText(entry) {
  if (!entry) return '';
  var detail = entry.detail || [];
  if ((entry.key === 'allergen' || entry.key === 'dislike') && detail.length) {
    return entry.label + ': ' + detail.join(', ');
  }
  return entry.label;
}

/**
 * Replace Both (pair alternatives) rows: each candidate's conflicts, whether
 * it is clean, and the "Closest match" mark.
 *
 * The backend lists conflict-free candidates first in the ranker's order --
 * exactly the set this dialog showed before Phase 2B -- and appends the
 * demoted conflicted ones. "Closest match" therefore keeps its meaning by
 * being judged on the SHOWN CLEAN candidates only: when none of them keeps
 * both days in range, the first clean one is the closest match. A conflicted
 * candidate can never take or suppress that label (it still shows its own
 * "keeps both days" fact and its conflicts).
 */
export function annotatePairCandidates(candidates) {
  var rows = (candidates || []).map(function (cand) {
    var conflicts = getMealConflicts(cand, { scope: 'pair' });
    return { candidate: cand, conflicts: conflicts, clean: conflicts.length === 0,
      keeps: !!(cand && cand.keeps_both_in_range), closest: false };
  });
  var clean = rows.filter(function (r) { return r.clean; });
  var cleanKeeps = clean.some(function (r) { return r.keeps; });
  if (!cleanKeeps && clean.length > 0) clean[0].closest = true;
  return rows;
}
